import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import ImageUploader from 'components/imageUploader/imageUploader';
import { showLoader, hideLoader } from 'store/uiSlice';

interface UploadMethodImageModalProps {
  open: boolean;
  onClose: () => void;
  methodId: number | null;
  methodName: string;
  onSuccess: (message: string, type: 'success' | 'error') => void;
  uploadFn: (id: number, file: File) => Promise<any>;
}

const UploadMethodImageModal: React.FC<UploadMethodImageModalProps> = ({
  open,
  onClose,
  methodId,
  methodName,
  onSuccess,
  uploadFn,
}) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImageSelect = (file: File | null) => {
    setSelectedFile(file);
    setError(null);
  };

  const handleSave = async () => {
    if (!methodId || !selectedFile) {
      setError('Please select an image first');
      return;
    }

    setLoading(true);
    setError(null);
    dispatch(showLoader({ text: 'Uploading icon...' }));
    try {
      const response = await uploadFn(methodId, selectedFile);
      onSuccess(response.data?.message || 'Image uploaded successfully', 'success');
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload image');
    } finally {
      setLoading(false);
      dispatch(hideLoader());
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: 'var(--text-primary)', backgroundColor: 'var(--bg-panel)' }}>
        Upload Image for {methodName}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: 'var(--bg-panel)', pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
          <ImageUploader onImageSelect={handleImageSelect} />
          {error && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: 'var(--bg-panel)', px: 3, pb: 3 }}>
        <Button onClick={handleClose} sx={{ color: 'var(--text-secondary)' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedFile || loading}
          sx={{
            backgroundColor: '#00b3f5',
            '&:hover': { backgroundColor: '#0090c5' },
            minWidth: 100,
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadMethodImageModal;
